import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
// The one shared list — never a local copy of the palette. A second copy is
// exactly how Arjan came out grey instead of JVC's brown (2026-08-30).
import { colorOf as colorFor } from '../../../constants/areas';
// The same "can we take this one?" box the leads coverage map carries — it
// reads the same boundaries, so it belongs on both maps rather than one.
import ZoneCheck from '../Leads/ZoneCheck';
import './statistics.css';
import './coverageStat.css';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const DUBAI = { lat: 25.14, lng: 55.23 };

/**
 * Every zone we cover, and every active property we clean, on one map.
 *
 * The shapes are the published community boundaries (OpenStreetMap), the same
 * cache and the same palette the leads coverage map and the scheduling board
 * use — an area is the same colour wherever it appears.
 *
 * The pins are EVERY active property, including the ones that fall outside a
 * covered area ("even the ones outside of our areas"). Those are drawn in a
 * deliberately different, darker style: on a map about coverage, a unit we
 * clean somewhere we do not officially cover is the interesting one, not an
 * error to hide.
 */
export default function CoverageStat() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPins, setShowPins] = useState(true);
  const [showZones, setShowZones] = useState(true);
  const [hover, setHover] = useState(null);
  // A looked-up address, flagged on the map.
  const [checked, setChecked] = useState(null);
  const tipRef = React.useRef(null);
  // True while the cursor is over a PROPERTY. The zone underneath keeps firing
  // mousemove the whole time, and it used to overwrite the pin's label the
  // instant it appeared — which is why hovering a pin only ever showed the area
  // (2026-08-30). The pin wins for as long as it is under the cursor.
  const pinLock = React.useRef(false);

  // Move the label without re-rendering. Returns nothing; callers just place it.
  const placeTip = React.useCallback((domEvent, lineCount) => {
    const el = tipRef.current;
    if (!el) return;
    const box = el.parentElement?.getBoundingClientRect?.();
    const x = (domEvent?.clientX ?? 0) - (box?.left ?? 0);
    const y = (domEvent?.clientY ?? 0) - (box?.top ?? 0);
    const W = 250;
    const H = 20 + 18 * ((lineCount || 0) + 1);
    const left = box && x + 14 + W > box.width ? Math.max(6, x - 14 - W) : x + 14;
    const top = box && y + 14 + H > box.height ? Math.max(6, y - 14 - H) : y + 14;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, []);

  // Only re-render when the label's CONTENT changes.
  const showTip = React.useCallback((next) => {
    setHover((prev) => {
      if (!next && !prev) return prev;
      if (next && prev && prev.title === next.title
        && prev.lines.join('|') === next.lines.join('|')) return prev;
      return next;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    axios.get('/api/leads/coverage', { params: { points: 1 } })
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch((e) => {
        if (!alive) return;
        setError(e.response?.data?.error || 'Could not load the coverage map.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const points = useMemo(() => data?.points || [], [data]);
  const outside = useMemo(() => points.filter((p) => p.outside), [points]);
  // Filed under one area, pinned well inside another. The saved area is what
  // every count, route and report uses — so a disagreement this large is a
  // data-entry error worth naming, not a rounding of the map.
  const misfiled = useMemo(() => points.filter((p) => p.mismatch), [points]);
  const areas = useMemo(
    () => [...(data?.areas || [])].sort((a, b) => b.properties - a.properties),
    [data]
  );

  if (!GMAPS_KEY) {
    return (
      <div className="covst__err">
        The map cannot load — REACT_APP_GOOGLE_MAPS_KEY is not set in this build.
      </div>
    );
  }

  return (
    <div className="covst">
      <div className="covst__bar">
        <div className="covst__counts">
          {loading ? (
            <span className="covst__countsLoading">Loading the map…</span>
          ) : (
            <>
              <span className="covst__count">
                <strong>{points.length}</strong> active properties
              </span>
              <span className="covst__countSep">·</span>
              <span className="covst__count">
                <strong>{points.length - outside.length}</strong> inside our areas
              </span>
              <span className="covst__countSep">·</span>
              <span className="covst__count covst__count--outside">
                <strong>{outside.length}</strong> outside
              </span>
              {misfiled.length > 0 && (
                <>
                  <span className="covst__countSep">·</span>
                  <span className="covst__count covst__count--misfiled">
                    <strong>{misfiled.length}</strong> in the wrong area
                  </span>
                </>
              )}
              <span className="covst__countSep">·</span>
              <span className="covst__count">
                <strong>{areas.length}</strong> zones
              </span>
            </>
          )}
        </div>

        <div className="covst__toggles">
          <label className="covst__toggle">
            <input
              type="checkbox"
              checked={showZones}
              onChange={(e) => setShowZones(e.target.checked)}
            />
            Zones
          </label>
          <label className="covst__toggle">
            <input
              type="checkbox"
              checked={showPins}
              onChange={(e) => setShowPins(e.target.checked)}
            />
            Property pins
          </label>
        </div>
      </div>

      {error && <div className="covst__err">{error}</div>}

      <ZoneCheck onPick={setChecked} />

      <div className="covst__map">
        {/* Positioned by direct DOM write, not React state (2026-08-30). A
            polygon's mousemove fires many times a second; routing each one
            through setState re-rendered this whole component — legend, outside
            list and all — and made the map feel sluggish the moment pins were
            on. State now changes only when the hovered THING changes. */}
        <div
          ref={tipRef}
          className="covst__tip"
          style={{ display: hover ? 'flex' : 'none' }}
        >
          <strong>{hover?.title}</strong>
          {(hover?.lines || []).map((l) => <span key={l}>{l}</span>)}
        </div>
        <APIProvider apiKey={GMAPS_KEY}>
          <Map
            mapId={MAP_ID}
            defaultCenter={DUBAI}
            defaultZoom={10}
            disableDefaultUI
            zoomControl
            gestureHandling="greedy"
          >
            <FitToProperties points={points} />
            {checked && (
              <AdvancedMarker position={{ lat: checked.lat, lng: checked.lng }}>
                <span className="lds__checkFlag">
                  <span className="lds__checkFlagLabel">{checked.label}</span>
                  <span className="lds__checkFlagStem" />
                  <span className="lds__checkFlagDot" />
                </span>
              </AdvancedMarker>
            )}
            {showZones && (
              <Shapes areas={data?.areas || []} showTip={showTip} placeTip={placeTip} pinLock={pinLock} />
            )}
            {showPins && (
              <PropertyPins points={points} showTip={showTip} placeTip={placeTip} pinLock={pinLock} />
            )}
          </Map>
        </APIProvider>
      </div>

      <div className="covst__legend">
        {areas.map((a) => (
          <span className="covst__legendItem" key={a.area}>
            <span
              className={`covst__legendDot${a.approx ? ' covst__legendDot--approx' : ''}`}
              style={a.approx
                ? { background: 'transparent', borderColor: colorFor(a.area) }
                : { background: colorFor(a.area) }}
            />
            {a.area} <strong>{a.properties}</strong>
            {a.approx && <span className="covst__legendApprox">approx.</span>}
          </span>
        ))}
        {outside.length > 0 && (
          <span className="covst__legendItem covst__legendItem--outside">
            <span className="covst__legendDot covst__legendDot--outside" />
            Outside our areas <strong>{outside.length}</strong>
          </span>
        )}
        {misfiled.length > 0 && (
          <span className="covst__legendItem covst__legendItem--misfiled">
            <span className="covst__legendDot covst__legendDot--misfiled" />
            Saved in the wrong area <strong>{misfiled.length}</strong>
          </span>
        )}
      </div>

      {misfiled.length > 0 && (
        <div className="covst__outsideList covst__misfiledList">
          <div className="covst__outsideTitle">Possibly saved in the wrong area</div>
          <p className="covst__misfiledNote">
            Every count on this page comes from the area each property is SAVED
            under. These are pinned well inside a different one — far enough that
            a boundary disagreement does not explain it. Fix the property, not the map.
          </p>
          <ul>
            {misfiled.map((p) => (
              <li key={p.id}>
                <a href={`/properties/${p.id}`} target="_blank" rel="noreferrer">
                  {[p.unit, p.building].filter(Boolean).join(' · ') || `Property #${p.id}`}
                </a>
                <span className="covst__outsideMeta">
                  saved as <strong>{p.area}</strong> · sits in <strong>{p.sits_in}</strong>
                  {p.metres_from_own_area != null
                    ? ` · ${p.metres_from_own_area.toLocaleString()} m away`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {outside.length > 0 && (
        <div className="covst__outsideList">
          <div className="covst__outsideTitle">Properties outside our areas</div>
          <ul>
            {outside.map((p) => (
              <li key={p.id}>
                <a href={`/properties/${p.id}`} target="_blank" rel="noreferrer">
                  {[p.unit, p.building].filter(Boolean).join(' · ') || `Property #${p.id}`}
                </a>
                <span className="covst__outsideMeta">
                  {p.area || 'no area set'}{p.client_name ? ` — ${p.client_name}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Frame the city we actually work in, once.
 *
 * A fixed centre and zoom put half the map over empty desert and the properties
 * in one corner. Fitting the pins — the outside-area ones included, they are
 * the far corners — opens the page on the thing it is about. Runs ONCE: after
 * that the view is the viewer's, and re-fitting on every toggle would yank the
 * map back from wherever they had panned to.
 */
function FitToProperties({ points }) {
  const map = useMap();
  const done = React.useRef(false);

  useEffect(() => {
    if (!map || done.current || !points.length || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 48);
    done.current = true;
  }, [map, points]);

  return null;
}

/**
 * The zone shapes.
 *
 * @vis.gl/react-google-maps ships neither Polygon nor Circle, so the overlays
 * are created against the map instance and torn down with it — leaving them
 * attached is how a map ends up with four copies of every area after a few
 * toggles.
 */
function Shapes({ areas, showTip, placeTip, pinLock }) {
  const map = useMap();

  // Smallest drawn last so it sits on top: whatever is on top is what the
  // cursor reaches, and a member shape (Zabeel inside Downtown) is drawn whole.
  const list = useMemo(() => {
    const size = (a) => (a.boundary || [])
      .reduce((t, ring) => t + Math.abs(ring.reduce((s, v, i, r) => {
        const w = r[(i + 1) % r.length];
        return s + (v.lng * w.lat - w.lng * v.lat);
      }, 0) / 2), 0) || (a.radius || 0) / 1e6;
    return [...areas].sort((a, b) => size(b) - size(a));
  }, [areas]);

  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    const drawn = list.map((a) => {
      const color = colorFor(a.area);
      const style = {
        map,
        strokeColor: color,
        strokeOpacity: a.approx ? 0.75 : 0.95,
        strokeWeight: 2,
        fillColor: color,
        // An approximate disc is drawn lighter than a real boundary, so the two
        // never read as the same kind of claim.
        fillOpacity: a.approx ? 0.10 : 0.16,
        clickable: true,
        // Strictly below the property layer (900/1000) so a pin inside a zone
        // is always the thing the cursor finds.
        zIndex: 10,
      };
      const shape = a.boundary?.length
        ? new window.google.maps.Polygon({ ...style, paths: a.boundary })
        : a.polygon && a.polygon.length >= 3
          ? new window.google.maps.Polygon({ ...style, paths: a.polygon })
          : new window.google.maps.Circle({
            ...style, center: { lat: a.lat, lng: a.lng }, radius: a.radius,
          });

      const lines = [
        `${a.properties} active ${a.properties === 1 ? 'property' : 'properties'}`,
        ...(a.shape !== 'boundary' ? ['outline from our own units'] : []),
        ...(a.approx ? ['approximate — no published boundary'] : []),
      ];
      shape.addListener('mousemove', (e) => {
        // A property under the cursor owns the label; the zone is the backdrop.
        if (pinLock.current) return;
        shape.setOptions({ fillOpacity: 0.34 });
        placeTip(e.domEvent, lines.length);
        showTip({ title: a.area, lines });
      });
      shape.addListener('mouseout', () => {
        shape.setOptions({ fillOpacity: 0.16 });
        if (!pinLock.current) showTip(null);
      });
      return shape;
    });

    return () => drawn.forEach((sh) => {
      window.google.maps.event.clearInstanceListeners(sh);
      sh.setMap(null);
    });
  }, [map, list, showTip, placeTip, pinLock]);

  return null;
}

/**
 * The properties.
 *
 * ~400 google.maps.Marker objects made the page crawl: each one is a managed
 * overlay with its own DOM, listeners and hit-testing, all re-projected on
 * every pan and zoom ("statistics with property pins its extremely slow",
 * 2026-08-30).
 *
 * One Data layer replaces them. It draws every point in a single pass, keeps
 * the styling in one callback instead of 400 closures, and gives us hover for
 * free — and, crucially, it sits ABOVE the zone polygons, so pointing at a pin
 * inside an area reaches the pin rather than the shape underneath it ("
 * properties should be above the area itself so we can hover and see which one
 * is which").
 */
function PropertyPins({ points, showTip, placeTip, pinLock }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google?.maps || !points.length) return undefined;

    const layer = new window.google.maps.Data({ map });
    points.forEach((p) => {
      layer.add({
        geometry: new window.google.maps.Data.Point({ lat: p.lat, lng: p.lng }),
        properties: { p },
      });
    });

    // THE STYLE IS BUILT ONCE PER PIN, NOT PER FRAME (2026-08-30).
    //
    // Data.setStyle(fn) runs fn for EVERY feature on EVERY viewport change, so
    // a callback that allocated an icon object and looked up an area colour
    // cost 396 allocations and 396 lookups per pan and per zoom step.
    // Measured: a 52–73 ms long task on every zoom with pins on, and none at
    // all with them off. Now the object is computed once, cached on the
    // feature, and simply handed back — nothing is allocated while the map
    // moves.
    layer.forEach((feature) => {
      const p = feature.getProperty('p');
      feature.setProperty('style', {
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: p.outside ? 6.5 : (p.mismatch ? 6 : 4.5),
          fillColor: p.outside ? '#111827' : (colorFor(p.area) || '#2563eb'),
          fillOpacity: 1,
          // Ringed in warning amber, but still filled in its SAVED area's
          // colour — the counts use the saved area; the ring is the question.
          strokeColor: p.mismatch ? '#f59e0b' : '#ffffff',
          strokeWeight: p.outside ? 2 : (p.mismatch ? 2.5 : 1.2),
        },
        // Above every zone shape, always: a pin you cannot point at is a pin
        // that may as well not be drawn.
        zIndex: p.outside ? 1000 : (p.mismatch ? 950 : 900),
        clickable: true,
        cursor: 'pointer',
      });
    });

    layer.setStyle((feature) => feature.getProperty('style'));

    const label = (p) => ({
      title: [p.unit, p.building].filter(Boolean).join(' · ') || `Property #${p.id}`,
      lines: [
        p.client_name || '',
        p.outside ? `${p.area || 'No area set'} — outside our areas` : (p.area || ''),
        p.mismatch ? `⚠ saved as ${p.area}, but sits in ${p.sits_in}` : '',
      ].filter(Boolean),
    });
    const over = layer.addListener('mouseover', (e) => {
      const p = e.feature.getProperty('p');
      pinLock.current = true;          // the zone below must stop overwriting us
      const l = label(p);
      placeTip(e.domEvent, l.lines.length);
      showTip(l);
    });
    // The Data layer emits mousemove too; keeping the label under the cursor
    // costs nothing extra now that position is a direct style write.
    const move = layer.addListener('mousemove', (e) => {
      const p = e.feature.getProperty('p');
      pinLock.current = true;
      placeTip(e.domEvent, label(p).lines.length);
    });
    const out = layer.addListener('mouseout', () => {
      pinLock.current = false;
      showTip(null);
    });
    // Open the property itself. A new tab, deliberately: you are usually
    // working through several pins and losing the map each time would make
    // this worse, not better (2026-08-30).
    const click = layer.addListener('click', (e) => {
      const p = e.feature.getProperty('p');
      if (p?.id) window.open(`/properties/${p.id}`, '_blank', 'noopener');
    });

    return () => {
      pinLock.current = false;
      window.google.maps.event.removeListener(over);
      window.google.maps.event.removeListener(move);
      window.google.maps.event.removeListener(click);
      window.google.maps.event.removeListener(out);
      layer.forEach((f) => layer.remove(f));
      layer.setMap(null);
    };
  }, [map, points, showTip, placeTip, pinLock]);

  return null;
}
